// A registry of full-editor-area "main views" contributed by plugins. Mirrors
// SidebarPanelRegistry, but for views that REPLACE the editor area (not the
// sidebar): the Git client is the first such view. Unlike sidebar panels,
// registering an existing id UPSERTS (replaces) instead of throwing, so a
// plugin that re-activates cleanly swaps its view. Consumed via
// `useMainViews` (useSyncExternalStore), so reads return a stable snapshot.

import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface MainViewItem {
  /** Stable id, e.g. 'git'. */
  id: string;
  /** Activity-bar switcher tooltip / aria-label. */
  title: string;
  /** Activity-bar switcher icon. */
  icon: LucideIcon;
  /** Ordering among main views (ascending); defaults to 100. */
  order?: number;
  /** The full-editor-area view body. */
  Component: ComponentType;
}

const defs = new Map<string, MainViewItem>();
const listeners = new Set<() => void>();

// `items` is consumed via `useSyncExternalStore`, which requires a
// referentially-stable snapshot between mutations (a fresh array every call
// would loop forever). Cache the sorted snapshot; clear it on every mutation.
let snapshot: readonly MainViewItem[] | null = null;

function emit(): void {
  snapshot = null;
  listeners.forEach((l) => l());
}

export const MainViewRegistry = {
  /**
   * Register (or replace, by id) a main-area view. Returns a disposer that
   * removes exactly this registration; a disposer for a since-replaced item is
   * a no-op so it cannot clobber the replacement.
   */
  register(item: MainViewItem): () => void {
    defs.set(item.id, item);
    emit();
    return () => {
      if (defs.get(item.id) === item) {
        defs.delete(item.id);
        emit();
      }
    };
  },
  items(): readonly MainViewItem[] {
    if (snapshot === null) {
      snapshot = Array.from(defs.values()).sort(
        (a, b) => (a.order ?? 100) - (b.order ?? 100) || a.title.localeCompare(b.title),
      );
    }
    return snapshot;
  },
  getById(id: string): MainViewItem | undefined {
    return defs.get(id);
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

/** Test-only: clear the registry. */
export function _resetForTest(): void {
  defs.clear();
  listeners.clear();
  snapshot = null;
}
